"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { AlertTriangle, RefreshCw, Home, WifiOff } from "lucide-react"
import Link from "next/link"

interface BlogErrorFallbackProps {
  error: Error | null
  context?: string
  onRetry?: () => void
  retryCount?: number
}

export function BlogErrorFallback({ 
  error, 
  context = 'loading content',
  onRetry,
  retryCount = 0
}: BlogErrorFallbackProps) {
  const isNetworkError = error?.message?.toLowerCase().includes('fetch') || 
                         error?.message?.toLowerCase().includes('network') ||
                         error?.message?.toLowerCase().includes('failed to fetch')

  return (
    <div className="min-h-[400px] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white shadow-xl border-red-100">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            {isNetworkError ? (
              <WifiOff className="w-8 h-8 text-red-600" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-red-600" />
            )}
          </div>
          <CardTitle className="text-xl font-bold text-gray-900">
            {isNetworkError ? 'Connection Problem' : 'Something Went Wrong'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-600">
            {isNetworkError 
              ? "We're having trouble connecting to our servers. Please check your internet connection and try again."
              : `We encountered an error while ${context}. Don't worry, your data is safe.`}
          </p>
          
          {retryCount > 0 && (
            <p className="text-sm text-gray-500">
              Retry attempt: {retryCount} of 3
            </p>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3">
            {onRetry && (
              <Button
                onClick={onRetry}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
            
            <Button
              asChild
              variant="outline"
              className="flex-1"
            >
              <Link href="/">
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Link>
            </Button>
          </div>

          {process.env.NODE_ENV === 'development' && error && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                Error Details (Development)
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 overflow-auto">
                <div className="mb-2">
                  <strong>Error:</strong> {error.message}
                </div>
                {error.stack && (
                  <div>
                    <strong>Stack:</strong>
                    <pre className="whitespace-pre-wrap mt-1">
                      {error.stack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

